import { getCorrectTypeColor } from '@repo/lib/quiz/getCorrectTypeColor';
import { QuestionSingle } from '@repo/lib/quiz/questions/class/QuestionSingle';
import { PencilLineIcon } from 'lucide-react';
import { useMemo } from 'react';

import {
  QuestionMarkedDescription,
  QuestionMarkedHeader,
  QuestionMarkedNoAnswer,
  QuestionMarkedQuestionContent,
  QuestionMarkedUserAnswer,
  QuestionMarkedWrapper,
} from '@/components/features/quiz/question/question-marked';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Markdown } from '@/components/ui/markdown';
import { CorrectIcon } from '@/components/ui/quiz';
import { useQuestionMarkedForm } from '@/hooks/questions';

import type { QuestionMarkedSingleProps } from '.';

import { QuestionMarkedSingleForm } from '.';
import { QuestionMarkedSingleContent } from './QuestionMarkedSingleContent';

export function QuestionMarkedSingle(questionMarkedSingleProps: QuestionMarkedSingleProps) {
  const { marked, question, showQuestion, showMarked, answer, onEditMark } = questionMarkedSingleProps;
  const { formRef, wrapperRef, openForm, setOpenForm, openFormHandler, closeFormHandler } = useQuestionMarkedForm();
  const questionObj = useMemo<QuestionSingle>(() => {
    const qClass = new QuestionSingle(question, null, marked);
    if (answer) qClass.importAnswer(answer);
    return qClass;
  }, [question, marked, answer]);

  const correctState = questionObj.correctState();
  const userAnswerContent: string | undefined = question.answers.find((a) => a.id === marked?.id)?.content;

  if (!showMarked) return null;

  if (!marked || !questionObj.isMarked()) return <QuestionMarkedNoAnswer showMarked={showMarked} />;
  return (
    <QuestionMarkedWrapper showMarked={showMarked} ref={wrapperRef}>
      <QuestionMarkedHeader
        score={questionObj.getPoints()}
        totalPoints={questionObj.getMaxPoints()}
        correct={marked.correct ?? false}
        correctType={correctState}
        headerActions={
          onEditMark && (
            <Button
              variantColor={correctState === 'waiting' ? 'destructive' : 'default'}
              variant={correctState === 'waiting' ? 'secondary' : 'tertiary'}
              size="sm"
              disabled={openForm}
              onClick={openFormHandler}
            >
              <PencilLineIcon />
              Oceń
            </Button>
          )
        }
      />
      <QuestionMarkedQuestionContent showQuestion={showQuestion} content={question.content}>
        <QuestionMarkedSingleContent question={question} />
      </QuestionMarkedQuestionContent>
      {userAnswerContent && !openForm && (
        <QuestionMarkedUserAnswer>
          <div className="w-full space-y-1">
            <div className="space-y-2">
              <p className="text-accent-foreground text-sm font-medium">Odpowiedziano:</p>
              <div className="flex items-center gap-1">
                <CorrectIcon answerState={correctState} size={'sm'} />
                <Markdown content={userAnswerContent} textClassName={getCorrectTypeColor(correctState)} imageClassName="max-h-68 rounded-sm" videoClassName="max-h-68 rounded-sm" />
              </div>
            </div>
            <p className="text-muted-foreground ml-5 text-sm empty:hidden">{marked.explanation}</p>
          </div>
        </QuestionMarkedUserAnswer>
      )}
      <Collapsible open={openForm} onOpenChange={setOpenForm} className="-my-px">
        <CollapsibleContent ref={formRef}>
          <QuestionMarkedDescription />
          <QuestionMarkedSingleForm
            onSubmit={onEditMark}
            onClose={closeFormHandler}
            marked={questionObj.getMarked()}
            userAnswerContent={userAnswerContent}
            isOpen={openForm}
            maxPoints={questionObj.getMaxPoints()}
          />
        </CollapsibleContent>
      </Collapsible>
    </QuestionMarkedWrapper>
  );
}
